import { NavLink, Outlet, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { useAuth } from "../features/auth/AuthProvider";
import { useEnsureRequiredGoals } from "../features/goals/requiredGoals";

/**
 * Bottom tab bar, per the Profile tab handoff: Lucide icons over Geist labels,
 * the active tab in near-black and the rest in muted grey.
 */
const TABS = [
  {
    to: "/",
    label: "Feed",
    end: true,
    // Lucide "users"
    icon: (
      <>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </>
    ),
  },
  {
    to: "/goals",
    label: "Goals",
    end: false,
    // Lucide "circle-check"
    icon: (
      <>
        <circle cx="12" cy="12" r="10" />
        <path d="m9 12 2 2 4-4" />
      </>
    ),
  },
  {
    to: "/me",
    label: "Profile",
    end: false,
    // Lucide "user"
    icon: (
      <>
        <circle cx="12" cy="8" r="5" />
        <path d="M20 21a8 8 0 0 0-16 0" />
      </>
    ),
  },
];

export function AppShell({ banner }: { banner?: ReactNode }) {
  const { userId } = useAuth();
  useEnsureRequiredGoals(userId);

  // The profile screen sits on the pastel marble; everything else is plain grey.
  const { pathname } = useLocation();
  const marble = pathname === "/me" || pathname.startsWith("/people/");

  return (
    <div
      className="min-h-dvh bg-[#f8f8fa] bg-cover bg-center bg-no-repeat font-['Geist',system-ui,sans-serif] text-[#0a0a0b]"
      style={marble ? { backgroundImage: "url(/marble.jpg)", backgroundAttachment: "fixed" } : undefined}
    >
      {banner}

      {/* Clears the tab bar (60px + safe area) plus 16px of air. */}
      <main className="mx-auto w-full max-w-lg px-5 pb-28">
        <Outlet />
      </main>

      <nav
        aria-label="Main"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-[#ececef] bg-[rgba(251,251,252,0.88)] font-['Geist',system-ui,sans-serif] backdrop-blur-[12px]"
      >
        <ul className="mx-auto grid h-[calc(60px+env(safe-area-inset-bottom))] max-w-lg grid-cols-3 pb-[env(safe-area-inset-bottom)]">
          {TABS.map(({ to, label, end, icon }) => (
            <li key={to}>
              {/* NavLink sets aria-current="page" on the active tab. */}
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    "flex h-full flex-col items-center justify-center gap-1 text-[11px] font-medium no-underline transition-[color,transform] duration-150 active:scale-[0.98]",
                    isActive ? "text-[#0a0a0b]" : "text-[#8a8a94]",
                  )
                }
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  {icon}
                </svg>
                <span>{label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * The wordmark: lowercase Geist semibold, in the near-black primary.
 */
export function Wordmark({ size = "sm" }: { size?: "sm" | "lg" }) {
  return (
    <span
      className={cn(
        "font-heading font-semibold lowercase tracking-[-0.03em] text-text",
        size === "lg" ? "text-[32px]" : "text-lg",
      )}
    >
      our pact
    </span>
  );
}

/** The back chevron used by every screen below the tab bar. */
export function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Back"
      className="-ml-2 rounded-full p-2 text-lg leading-none opacity-50 transition-opacity hover:opacity-100"
    >
      <span aria-hidden>←</span>
    </button>
  );
}
